// Gallery picker dialog — pulls saved Sense AI / Dashboard items so the
// user can drop them into the active dashboard. Extracted from
// `pages/Dashboards.jsx` (iter 41 refactor) — no behaviour change.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Plus, BarChart2, Table as TableIcon, Sparkles } from "lucide-react";


// Map a Gallery item (saved from Sense AI or elsewhere) into a Dashboard
// widget shape. Returns null if the item's kind can't be rendered.
export function galleryItemToWidget(item) {
    // iter-119ah — gallery items saved via `_decompose_widgets_to_gallery`
    // (the Builder's "publish to gallery" path) stash the FULL original
    // widget object under `item.widget`, plus `data_model_bindings` at
    // the item root. The legacy save path used `item.config` but that
    // hasn't carried real binding data for prebuilt-derived items — so
    // we ALWAYS preferred the richer `item.widget` when available and
    // fall back to `config` only for legacy fixtures.
    //
    // Without this fix, "Add from Gallery" on a blank dashboard would
    // PATCH a widget with `config: {auto:false, from_gallery:...}` and
    // NOTHING ELSE → the backend's `_is_blank_widget` correctly dropped
    // the row (no formula, no model, no x/y, no kpis) → nothing visible
    // appeared on the dashboard. Same root cause for "preview shows only
    // title" on the Gallery page.
    const w = item.widget || {};
    const cfg = item.config || {};
    const id = `gw_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const mergedTitle = item.name || w.title || "Untitled";
    if (item.kind === "kpi") {
        if (w.formula || w.kpis || w.metric_field) {
            return {
                ...w, id, title: mergedTitle, type: "kpi_card",
                config: { ...(w.config || {}), ...cfg, auto: false, from_gallery: item.id },
                data_model_bindings: item.data_model_bindings || w.data_model_bindings,
            };
        }
        return { id, type: "kpi_card", title: mergedTitle,
                 config: { ...cfg, auto: false, from_gallery: item.id } };
    }
    if (item.kind === "chart") {
        if (w.formula || w.chart_type || w.x || w.y || w.column || w.metric_field) {
            return {
                ...w, id, title: mergedTitle, type: "chart",
                chart_type: w.chart_type || cfg.chart_type || "bar",
                config: { ...(w.config || {}), ...cfg, auto: false, from_gallery: item.id },
                data_model_bindings: item.data_model_bindings || w.data_model_bindings,
            };
        }
        return { id, type: "chart", title: mergedTitle,
                 config: { ...cfg, auto: false, from_gallery: item.id } };
    }
    if (item.kind === "table") {
        if (w.columns || w.formula || w.metric_field) {
            return {
                ...w, id, title: mergedTitle, type: "table",
                config: { ...(w.config || {}), ...cfg, auto: false, from_gallery: item.id },
                data_model_bindings: item.data_model_bindings || w.data_model_bindings,
            };
        }
        return { id, type: "table", title: mergedTitle,
                 config: { ...cfg, auto: false, from_gallery: item.id } };
    }
    if (item.kind === "insight") {
        const summary = cfg.structured?.summary || item.description || item.name;
        return { id, type: "kpi_card", title: mergedTitle,
                 config: { value: summary, unit: "", auto: false, from_gallery: item.id } };
    }
    return null;
}


export default function GalleryPickerDialog({ open, onClose, onAdd }) {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [kind, setKind] = useState("all");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/gallery")
           .then((r) => setItems(r.data || []))
           .catch(() => setItems([]))
           .finally(() => setLoading(false));
    }, [open]);

    const ALLOWED_KINDS = ["kpi", "chart", "table", "insight"];
    const kinds = ["all", ...ALLOWED_KINDS];
    const KIND_ICON = { kpi: BarChart2, chart: BarChart2, table: TableIcon, insight: Sparkles };
    const filtered = items.filter((i) =>
        // Only widget-class gallery items are eligible for "Add from Gallery" —
        // prebuilt dashboards (kind=prebuilt_dashboard) are NOT widgets and
        // cannot be embedded inside another dashboard. Keep the picker focused.
        ALLOWED_KINDS.includes(i.kind) &&
        (kind === "all" || i.kind === kind) &&
        (!q || (i.name || "").toLowerCase().includes(q.toLowerCase()))
    );

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Add from Gallery</DialogTitle></DialogHeader>
                <div className="flex items-center gap-2 mb-3">
                    <Input value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder="Search gallery..."
                           className="h-9 rounded-sm text-sm"
                           data-testid="gallery-picker-search" />
                    <div className="flex gap-1">
                        {kinds.map((k) => (
                            <button key={k} onClick={() => setKind(k)}
                                    className={`px-2.5 h-8 text-[10px] font-mono uppercase tracking-wider border rounded-sm ${
                                        kind === k ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"
                                    }`}
                                    data-testid={`gallery-picker-kind-${k}`}>{k}</button>
                        ))}
                    </div>
                </div>
                <div className="max-h-[420px] overflow-y-auto scrollbar-thin space-y-1.5 pr-1">
                    {loading ? (
                        <div className="text-xs text-muted-foreground py-8 text-center">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-8 text-center" data-testid="gallery-picker-empty">
                            {items.length === 0 ? "Save items from Sense AI or dashboards to see them here." : "No items match."}
                        </div>
                    ) : filtered.map((i) => {
                        const Icon = KIND_ICON[i.kind] || BarChart2;
                        return (
                            <button key={i.id}
                                    onClick={() => onAdd(i)}
                                    className="w-full flex items-center gap-3 p-3 border border-border rounded-sm hover:border-foreground/40 hover:bg-secondary/30 text-left"
                                    data-testid={`gallery-picker-item-${i.id}`}>
                                <div className="w-8 h-8 bg-secondary flex items-center justify-center shrink-0">
                                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{i.name}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{i.category || "general"} · {i.kind}</div>
                                </div>
                                <Plus className="h-4 w-4 text-muted-foreground" />
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
